import LearnLayout from "@/components/LearnLayout";
import { TimingList } from "@/components/learn/FeatureCard";
import { FeatureStack } from "@/components/learn/FeatureStack";
import { Star, CornerDownRight } from "lucide-react";

const EARN = [
  { label: "Buy & sell gear", sub: "Sell your old wheel or pedals, or find the best deal on your next upgrade." },
  { label: "Ask & answer", sub: "Post questions, help other racers, or reviews." },
  { label: "Connect SimHub & race", sub: "Link SimHub and log sessions. Race like you always do." },
  { label: "Hit milestones", sub: "Earn for achievements and the goals you reach on and off track." },
  { label: "Chat in the paddock", sub: "Share updates, your rig, stories, and achievements to grow your brand and reach." },
  { label: "Set up your profile", sub: "Complete your profile set up and build your sim racer profile." },
];

const STEPS = [
  { n: "01", title: "Do your thing", desc: "Race, trade, post, and help out: the stuff you'd do on the platform anyway." },
  { n: "02", title: "Rack up points", desc: "Every qualifying activity and achievement adds to your balance automatically." },
  { n: "03", title: "Climb the Leaderboard", desc: "Watch your standing rise on the SRC leaderboard as points stack." },
  { n: "04", title: "Claim rewards", desc: "Redeem your points for the rewards available in the points portal." },
];

function EarnBody() {
  return (
    <div>
      <p className="text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/80">
        Grid Points reward the things you already do. Simply connect on Sim Racing Connected and keep doing what you already do: show up, race, trade, and connect.
      </p>
      <TimingList className="mt-6" items={EARN} />
    </div>
  );
}

function RewardsBody() {
  const rewards = ["Discount codes", "Store credit", "Gift cards", "More rewards added over time"];
  return (
    <div>
      <p className="text-[0.963rem] leading-relaxed text-foreground/80">
        Trade your points for real rewards from the points portal, including upgrades, discounts, and even cash gift cards.
      </p>
      <TimingList className="mt-6" items={rewards} />
    </div>
  );
}

function LeaderboardBody() {
  const rows = [
    { pos: "P1", name: "Top racer", bar: "w-full" },
    { pos: "P2", name: "Rising star", bar: "w-4/5" },
    { pos: "P3", name: "Challenger", bar: "w-3/5" },
  ];
  return (
    <div>
      <p className="text-[0.963rem] leading-relaxed text-foreground/80">
        The SRC Leaderboard is like a platform wide points tournament. See where you stack up and climb the leaderboard to enjoy perks for points leaders.
      </p>
      <div className="mt-6 space-y-2.5">
        {rows.map((r) => (
          <div key={r.pos} className="flex items-center gap-3">
            <span className="w-8 font-mono text-[10px] font-bold uppercase tracking-widest text-primary/80">{r.pos}</span>
            <span className="w-24 text-xs text-foreground/80">{r.name}</span>
            <div className="h-2 flex-1 rounded-full bg-background/70">
              <div className={`h-full rounded-full bg-gradient-to-r from-primary to-primary/40 ${r.bar}`} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function NoGrindBody() {
  return (
    <p className="text-[0.963rem] leading-relaxed text-foreground/80">
      Points come from doing what you already need to do, not jumping through hoops or wasting your time.
    </p>
  );
}

function AutomaticBody() {
  return (
    <div>
      <p className="text-[0.963rem] leading-relaxed text-foreground/80">
        Qualifying actions and achievements are counted automatically and logged to your balance. You don&apos;t need to do anything manually.
      </p>
      <div className="mt-6 flex flex-wrap gap-2">
        {["Auto-tracked", "Transparent", "Always on"].map((t) => (
          <span key={t} className="rounded-full border border-border bg-background/70 px-3 py-1 text-xs text-foreground/80">
            <Star className="mr-1 inline h-3 w-3 text-primary" />{t}
          </span>
        ))}
      </div>
    </div>
  );
}

const FEATURES = [
  {
    pos: "01",
    eyebrow: "// EARN",
    title: "Earn points from what you already do",
    summary: "Points for the things you already need to do like racing, buying, selling, asking questions, and more.",
    content: <EarnBody />,
  },
  {
    pos: "02",
    eyebrow: "// REDEEM",
    title: "Cash in your points",
    summary: "Trade points for discount codes, store credit, and gift cards.",
    content: <RewardsBody />,
  },
  {
    pos: "03",
    eyebrow: "// STANDINGS",
    title: "Climb the leaderboard",
    summary: "A platform wide points tournament with perks unlocked for points leaders.",
    content: <LeaderboardBody />,
  },
  {
    pos: "04",
    eyebrow: "// FAIR",
    title: "No gimmicks or wasted effort",
    summary: "Points come from doing what you already do, not jumping through hoops.",
    content: <NoGrindBody />,
  },
  {
    pos: "05",
    eyebrow: "// AUTOMATIC",
    title: "Tracked for you",
    summary: "Points are earned automatically while using SRC. No special actions or applications needed.",
    content: <AutomaticBody />,
  },
];

export default function LearnGridPoints() {
  return (
    <LearnLayout
      testId="learn-grid-points"
      kicker=""
      title={<>Race, <span className="text-gradient-primary-soft">Get Rewarded</span></>}
      sub="Earn Grid Points for the things you already do: racing, buying or selling sim gear, asking questions, and so much more. Just connect SimHub and race, redeem your points for real rewards."
      heroImage="https://customer-assets-agu9un31.emergentagent.net/job_140d9c64-db2c-4907-8f79-3f079e2743ed/artifacts/wdd80hsj_PLSRC-SRC-Gridpoints.webp"
      heroBgSize="cover"
      heroBgPosition="center 30%"
      heroImageOpacity={1}
      heroOverlayColor="rgba(0,0,0,0.1)"
      bodyBg="#eceef1"
    >
      <section className="pl-12 sm:pl-14">
        <div className="relative z-20 inline-block -skew-x-[11deg] bg-primary px-4 py-2 shadow-[0_12px_30px_-12px_rgba(0,0,0,0.65)] sm:px-5 sm:py-2.5">
          <h2 className="skew-x-[11deg] font-display text-[1.94rem] font-bold italic uppercase leading-none text-white sm:text-[2.43rem]">
            How Grid Points work
          </h2>
        </div>
        <div className="relative z-10 -mt-3">
          <FeatureStack items={FEATURES} testId="grid-points-feature-stack" tabBadge largeBody />
        </div>
      </section>

      {/* Earn just by sim racing — carbon fiber band, full-bleed */}
      <section
        className="relative mt-16 overflow-hidden bg-carbon"
        style={{ width: "100vw", marginLeft: "calc(50% - 50vw)", marginRight: "calc(50% - 50vw)" }}
      >
        <div className="speed-hatch pointer-events-none absolute inset-0 opacity-40" />
        <div className="relative mx-auto max-w-7xl px-4 py-16 sm:px-6">
          <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase">
            <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
            Earn just by sim racing
          </h2>

          <div className="mt-5 space-y-4 pl-10">
            <p className="relative max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
              <CornerDownRight aria-hidden className="absolute -left-10 top-1 h-5 w-5 text-primary" />
              Connect SimHub and let your seat time earn your next sim upgrade. Every session you run feeds real telemetry into Sim Racing Connected, and your Grid Points balance grows automatically as you race, climb lap boards, and advance.
            </p>
            <p className="relative max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
              <CornerDownRight aria-hidden className="absolute -left-10 top-1 h-5 w-5 text-primary" />
              Then double dip: share those same achievements straight to the <strong>Paddock Banter</strong> feed to grow your brand and reach, and earn even more. One session, rewarded twice.
            </p>
            <p className="relative max-w-3xl text-[0.99rem] sm:text-[1.1rem] leading-relaxed text-foreground/90">
              <CornerDownRight aria-hidden className="absolute -left-10 top-1 h-5 w-5 text-primary" />
              Your SRC profile starts fresh the day you pair SimHub. Don&apos;t worry about an old hero PB you can&apos;t beat or the tracks you drove years ago, every session after pairing counts as a clean start, with new PBs, tracks, and achievements earning from the moment you connect.
            </p>
          </div>

          <div className="mt-8 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {[
              { t: "Climb lap boards", d: "Whether you are climbing lap boards or even just setting new PBs, your points grow as you get better." },
              { t: "Race & learn tracks", d: "Whether you need to learn a new track before your first track day or a tournament at a new track, your practice session will earn points for you." },
              { t: "SimHub telemetry", d: "Achievements are pulled straight from your SimHub telemetry, no manual logging or guesswork." },
              { t: "Double-dip & share", d: "Share the same achievements to the Paddock Banter feed and earn again while growing your reach." },
            ].map((f) => (
              <div key={f.t} className="rounded-lg border border-white/15 bg-background/40 p-4">
                <p className="font-display text-base font-bold uppercase">{f.t}</p>
                <p className="mt-2 text-sm leading-relaxed text-foreground/85">{f.d}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="mt-20">
        <h2 className="relative pl-5 font-display text-[2.06rem] font-bold uppercase" style={{ color: "#0b0b0d" }}>
          <span aria-hidden className="absolute left-0 top-1 h-[calc(100%-8px)] w-2 rounded-full bg-primary" />
          From laps to rewards
        </h2>
        <div className="mt-8 grid gap-6 md:grid-cols-4">
          {STEPS.map((s) => (
            <div key={s.n} className="rounded-lg border border-border bg-card p-6">
              <span className="font-display text-5xl font-bold text-primary/70">{s.n}</span>
              <h3 className="mt-2 font-display text-xl font-semibold">{s.title}</h3>
              <p className="mt-2 text-[0.99rem] sm:text-[1.1rem]" style={{ color: "#c7c8cf" }}>{s.desc}</p>
            </div>
          ))}
        </div>
      </section>
    </LearnLayout>
  );
}
